import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../utils/toast';
import Icon from '../AppIcon';

const UserProfileMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();

  const menuItems = [
    { label: 'Reading List', path: '/reading-list', icon: 'Bookmark' },
    { label: 'Admin Dashboard', path: '/admin-dashboard', icon: 'Shield', adminOnly: true },
    { label: 'Analytics', path: '/analytics-dashboard', icon: 'BarChart3', adminOnly: true },
    { label: 'Settings', path: '/account-settings', icon: 'Settings' },
  ];

  const adminMenuItems = [
    { label: 'Admin Dashboard', icon: 'Shield', path: '/admin-dashboard' },
    { label: 'Admin Settings', icon: 'Settings', path: '/admin-settings' },
    { label: 'Admin Notifications', icon: 'Bell', path: '/admin-notifications' },
    { label: 'Alert History', icon: 'Bell', path: '/admin-alerts' },
    { label: 'Analytics Dashboard', icon: 'BarChart3', path: '/analytics-dashboard' },
    { label: 'Developer Dashboard', icon: 'Code', path: '/developer-dashboard' }
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef?.current && !menuRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSignOut = async () => {
    setIsOpen(false);
    
    try {
      // Clear local storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Sign out from Supabase
      const { error } = await signOut();
      
      if (error) {
        showToast('Failed to sign out. Please try again.', 'error');
        return;
      }
      
      // Show success message
      showToast('Signed out successfully', 'success');
      
      // Redirect to homepage
      navigate('/homepage');
    } catch (err) {
      console.error('Sign out error:', err);
      showToast('An error occurred during sign out', 'error');
    }
  };

  // Get user display info
  const displayName = userProfile?.name || user?.email?.split('@')?.[0] || 'User';
  const displayEmail = user?.email || '';
  const initials = displayName?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2) || 'U';

  // Filter menu items based on user role
  const userRole = userProfile?.role || 'reader';
  const visibleMenuItems = menuItems?.filter(item => {
    if (item?.adminOnly) {
      return userRole === 'admin';
    }
    return true;
  });

  // If not authenticated, show sign in button
  if (!user) {
    return (
      <Link
        to="/login"
        className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors duration-250 font-interface font-medium text-sm"
      >
        Sign In
      </Link>
    );
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="user-profile-trigger"
        aria-label="User menu"
        aria-expanded={isOpen}
      >
        <div className="user-avatar">{initials}</div>
        <Icon
          name={isOpen ? 'ChevronUp' : 'ChevronDown'}
          size={20}
          color="var(--color-muted-foreground)"
          className="hidden lg:block"
        />
      </button>
      {isOpen && (
        <div className="user-profile-menu">
          <div className="user-profile-menu-header">
            <div className="user-profile-menu-name">{displayName}</div>
            <div className="user-profile-menu-email">{displayEmail}</div>
          </div>

          <div className="user-profile-menu-items">
            {visibleMenuItems?.map((item) => (
              <Link
                key={item?.path}
                to={item?.path}
                className="user-profile-menu-item"
                onClick={() => setIsOpen(false)}
              >
                <Icon name={item?.icon} size={20} color="var(--color-foreground)" />
                <span>{item?.label}</span>
              </Link>
            ))}

            <div className="user-profile-menu-divider" />

            <button
              className="user-profile-menu-item w-full text-left"
              onClick={handleSignOut}
            >
              <Icon name="LogOut" size={20} color="var(--color-error)" />
              <span className="text-error">Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileMenu;